import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';

import { CartItem, FormData } from '@/types/types';
import AXIOS from '@/util/axios';
import NavLink from '@/components/navigation/NavLink';
import { useAuth } from '@/hooks/useAuth';
import { paymentMethods } from '@/components/orders/paymentMethods';

export default function OrderConfirmation() {
  const [formData, setFormData] = useState<FormData | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>('');
  const { authState } = useAuth();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [items, setItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState<string>('');
  console.log('Token w authState:', authState?.token);

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/');
    const idFromUrl = pathSegments[pathSegments.length - 1];
    setOrderId(idFromUrl);

    const orderData = localStorage.getItem('orderData');
    if (orderData) {
      const parsedOrderData = JSON.parse(orderData);
      setFormData(parsedOrderData);
      setSelectedPaymentMethod(parsedOrderData.selectedPaymentMethod || '');
    } else {
      console.error('No order data found in localStorage');
    }

    getOrderData(idFromUrl);
  }, []);

  async function getOrderData(id: string) {
    try {
      const response = await AXIOS.get(`/api/orders/orders/${id}`);
      if (response.data) {
        setOrderStatus(response.data.status);
        setItems(response.data.items);
        setTotalPrice(response.data.totalPriceGross);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
    }
  }

  function getStatusLabel(status: string | null) {
    return (
      {
        paid: 'Zapłacone',
        completed: 'Zakończone',
        cancelled: 'Anulowane',
      }[status ?? ''] || 'Nowe'
    );
  }

  function getStatusColor(status: string | null) {
    return (
      {
        new: '#f7c948',
        paid: '#f7c948',
        completed: '#3aaf76',
        cancelled: '#e53935',
      }[status ?? ''] || '#acacac'
    );
  }

  function getBorderColor(status: string | null) {
    return (
      {
        new: '#d9a436',
        paid: '#d9a436',
        completed: '#2a8957',
        cancelled: '#b71c1c',
      }[status ?? ''] || '#555454'
    );
  }

  async function downloadSalesDocument() {
    if (orderId) {
      try {
        const response = await AXIOS.get(`/api/orders/orders/${orderId}/pdf`, {
          responseType: 'blob',
        });

        const pdfBlob = response.data;
        const pdfURL = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = pdfURL;
        link.setAttribute('download', `order_${orderId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error downloading sales document:', error);
      }
    }
  }

  return (
    <main className='bg-white min-h-[100vh] h-full flex flex-col sm:flex-row overflow-hidden'>
      <section className='flex flex-col sm:flex-row w-full'>
        <aside className='flex flex-col w-full sm:w-2/3 p-10 sm:p-20'>
          <div className='flex items-center justify-end'>
            <Typography
              variant='body1'
              gutterBottom
              style={{ color: '#333', marginRight: '10px' }}
            >
              Status zamówienia:
            </Typography>
            <div
              className='px-2 py-1 text-zinc-700 font-semibold text-xs rounded-md shadow-sm text-center'
              style={{
                backgroundColor: getStatusColor(orderStatus),
                border: `2px solid ${getBorderColor(orderStatus)}`,
              }}
            >
              {getStatusLabel(orderStatus)}
            </div>
          </div>
          <Typography
            variant='h4'
            gutterBottom
            sx={{
              color: '#333',
              marginBottom: '20px',
              marginTop: '20px',
              fontSize: { xs: '1.25rem', md: '2rem' },
            }}
          >
            Zamówienie numer:{' '}
            <span style={{ fontSize: '0.9em', color: '#5c5e5d' }}>
              {orderId || 'Ładowanie...'}
            </span>
          </Typography>
          {items?.map((item: CartItem, index: number) => (
            <div
              key={item.course.id || index}
              className='mb-4 flex items-center'
            >
              <img
                src={item.course.thumbnailUrl}
                alt={item.course.name}
                className='w-1/4 object-cover mr-4'
              />
              <div className='flex-1'>
                <Typography variant='h6'>{item.course.name}</Typography>
              </div>
              <Typography
                variant='h6'
                style={{ color: '#5c5e5d', fontWeight: 'bold' }}
              >
                {item.priceFinal}
              </Typography>
            </div>
          ))}

          <hr style={{ border: '1px solid #f56c42', width: '100%' }} />
          <div className='flex justify-between items-center mt-4 pt-2'>
            <div className='flex justify-center items-center gap-2 uppercase'>
              <Typography
                variant='h6'
                sx={{
                  color: '#333',
                  marginBottom: '20px',
                  marginTop: '20px',
                  fontSize: { xs: '1.1rem', md: '2rem' },
                }}
              >
                Kwota łączna
              </Typography>
              <Typography
                variant='caption'
                style={{ fontWeight: 'normal', color: '#5c5e5d' }}
              >
                z VAT
              </Typography>
            </div>
            <Typography
              variant='h6'
              sx={{
                color: '#5c5e5d',
                fontSize: { xs: '1.1rem', md: '2rem' },
              }}
            >
              {totalPrice} zł
            </Typography>
          </div>
        </aside>

        <aside className='flex flex-col justify-center w-full md:w-1/3 sm:w-2/3 bg-gray-100 p-20 md:p-10'>
          <Typography
            variant='h4'
            sx={{
              color: '#333',
              marginBottom: '10px',
              marginTop: '10px',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Dane odbiorcy:
          </Typography>
          {formData && (
            <div>
              <Typography>{formData.customerData.name}</Typography>
              <Typography>{formData.customerData.city}</Typography>
              <Typography>{formData.customerData.postalCode}</Typography>
              <Typography>{formData.customerData.street}</Typography>
              <Typography>{formData.customerData.houseNumber}</Typography>
              <Typography>{formData.customerData.phoneNumber}</Typography>
              <Typography>{formData.customerData.email}</Typography>
              {formData.customerData.type === 'company' && (
                <Typography>{formData.customerData.nip}</Typography>
              )}
            </div>
          )}
          <div className='flex justify-between items-center mt-6'>
            <Typography variant='h6'>Wybrana metoda płatności:</Typography>
            <div className='flex items-center'>
              {selectedPaymentMethod ? (
                paymentMethods
                  .filter((method) => method.name === selectedPaymentMethod)
                  .map((method) => (
                    <img
                      key={method.name}
                      src={method.imageUrl}
                      alt={method.name}
                      className='w-20 h-auto ml-2'
                    />
                  ))
              ) : (
                <Typography
                  variant='h6'
                  style={{ color: '#5c5e5d', fontWeight: 'bolder' }}
                >
                  Nie wybrano metody płatności
                </Typography>
              )}
            </div>
          </div>
          <p className='text-zinc-400 text-xs py-4'>
            Na adresie e-mail podanym podczas zakupu produktów znajdziesz
            potwierdzenie sprzedaży. Możesz je również pobrać w tym miejscu.
          </p>
          <div className='flex flex-col items-center justify-center w-full my-4 text-sm text-center'>
            <NavLink
              href=''
              onCommand={downloadSalesDocument}
              title='Pobierz dokument sprzedaży'
              className='px-2 py-1 bg-[#f78b67] border-2 border-[#e6663b] text-white font-semibold rounded-md shadow-sm hover:bg-[#f8784d]'
            />
          </div>
        </aside>
      </section>
    </main>
  );
}
